import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import notificationService from '../services/notificationService';
import { Provider, StateModel, Profession, Service, Request } from '../models';
import { FiSearch, FiMapPin, FiBriefcase, FiStar, FiCheck, FiChevronDown, FiArrowLeft, FiAward, FiZap, FiTrendingUp, FiSliders, FiDollarSign } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';

const ProvidersListScreen = () => {
  const navigate = useNavigate();
  const { user, userType } = useAuth();

  const [providers, setProviders] = useState([]);
  const [states, setStates] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [selectedStateId, setSelectedStateId] = useState(null);
  const [selectedProfessionId, setSelectedProfessionId] = useState(null);
  const [searching, setSearching] = useState(false);
  const [expandedProviders, setExpandedProviders] = useState(new Set());
  const [providerServices, setProviderServices] = useState({});
  const [walletBalance, setWalletBalance] = useState(0);

  useEffect(() => { loadData(); }, []);
  useEffect(() => { fetchWalletBalance(); }, [user]);

  const fetchWalletBalance = async () => {
    if (!user || user.isGuest) return;
    try {
      if (userType === 'provider') {
        const provider = await supabaseService.getProviderById(user.id);
        if (provider) setWalletBalance(provider.wallet || 0);
      } else {
        const client = await supabaseService.getClientById(user.id);
        if (client) setWalletBalance(client.wallet || 0);
      }
    } catch (e) {
      console.error('Error fetching wallet balance:', e);
    }
  };

  const loadData = async () => {
    try {
      const [statesData, professionsData] = await Promise.all([
        supabaseService.getStates(),
        supabaseService.getProfessions()
      ]);
      setStates(statesData);
      setProfessions(professionsData);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error('فشل تحميل البيانات');
    }
  };

  const searchProviders = async () => {
    try {
      setSearching(true);
      const providersData = await supabaseService.getProviders({
        stateId: selectedStateId,
        professionId: selectedProfessionId
      });
      providersData.sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0));
      setProviders(providersData);
    } catch (error) {
      console.error("Error searching providers:", error);
      toast.error('فشل البحث عن مقدمي الخدمات');
    } finally {
      setSearching(false);
    }
  };

  const toggleProviderExpansion = (providerId) => {
    const newExpanded = new Set(expandedProviders);
    if (newExpanded.has(providerId)) {
      newExpanded.delete(providerId);
    } else {
      newExpanded.add(providerId);
      if (!providerServices[providerId]) loadProviderServices(providerId);
    }
    setExpandedProviders(newExpanded);
  };

  const loadProviderServices = async (providerId) => {
    try {
      const servicesData = await supabaseService.getServicesForProvider(providerId);
      setProviderServices(prev => ({ ...prev, [providerId]: servicesData }));
    } catch (error) {
      console.error("Error loading provider services:", error);
    }
  };

  const createRequestForService = async (providerId, service) => {
    try {
      const currentUser = supabaseService.currentUser;
      if (!currentUser) return;
      const request = new Request({
        clientId: currentUser.id,
        providerId,
        serviceId: service.id,
        status: 'pending',
      });
      await supabaseService.createRequest(request);
      await notificationService.notifyNewRequest(providerId, request);
      toast.success('تم إرسال الطلب بنجاح!');
      setTimeout(() => navigate('/client'), 1000);
    } catch (error) {
      console.error("Error creating request:", error);
      toast.error(`فشل إرسال الطلب: ${error.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans" dir="rtl">

      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 h-14">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-xl hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <FiArrowLeft size={18} />
            </button>
            <div className="flex-1">
              <h1 className="text-base font-bold text-gray-900">البحث عن مزود خدمة</h1>
              <p className="text-xs text-gray-400">اعثر على أفضل المحترفين في منطقتك</p>
            </div>
            {/* ✅ زر الشحن + الرصيد */}
            {user && !user.isGuest && (
              <button
                onClick={() => navigate('/wallet')}
                className="px-2.5 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition active:scale-95 flex items-center gap-1"
                title="شحن المحفظة"
              >
                <FiDollarSign className="text-emerald-600 w-3.5 h-3.5" />
                <span className="text-[10px] font-bold text-emerald-700">
                  {walletBalance?.toLocaleString('ar-DZ') || '0'} د.ج
                </span>
                <span className="text-[10px] font-bold text-emerald-700">شحن</span>
              </button>
            )}
            {providers.length > 0 && (
              <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                {providers.length} نتيجة
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 space-y-5">

        {/* ── Search Card ── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 bg-gray-900 rounded-lg flex items-center justify-center">
              <FiSliders size={13} className="text-white" />
            </div>
            <span className="text-sm font-bold text-gray-800">تصفية النتائج</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            {/* State */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mb-1.5">
                <FiMapPin size={12} />
                الولاية
              </label>
              <div className="relative">
                <select
                  className="w-full h-10 pl-9 pr-3 text-sm border border-gray-200 rounded-xl bg-gray-50 text-gray-900 font-medium appearance-none focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all cursor-pointer hover:border-gray-300"
                  value={selectedStateId || ''}
                  onChange={(e) => setSelectedStateId(parseInt(e.target.value) || null)}
                >
                  <option value="">كل الولايات</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <FiChevronDown size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Profession */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mb-1.5">
                <FiBriefcase size={12} />
                المهنة
              </label>
              <div className="relative">
                <select
                  className="w-full h-10 pl-9 pr-3 text-sm border border-gray-200 rounded-xl bg-gray-50 text-gray-900 font-medium appearance-none focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all cursor-pointer hover:border-gray-300"
                  value={selectedProfessionId || ''}
                  onChange={(e) => setSelectedProfessionId(parseInt(e.target.value) || null)}
                >
                  <option value="">كل المهن</option>
                  {professions.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <FiChevronDown size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          <button
            onClick={searchProviders}
            disabled={searching}
            className="w-full h-10 bg-gray-900 hover:bg-gray-800 text-white text-sm font-bold rounded-xl flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {searching ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                جاري البحث...
              </>
            ) : (
              <>
                <FiSearch size={15} />
                بحث
              </>
            )}
          </button>
        </div>

        {/* ── Results ── */}
        {searching ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <div className="w-10 h-10 border-2 border-gray-900/20 border-t-gray-900 rounded-full animate-spin" />
            <p className="text-sm text-gray-500">جاري البحث...</p>
          </div>
        ) : providers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center">
              <FiSearch size={24} className="text-gray-400" />
            </div>
            <p className="text-base font-bold text-gray-900">لا توجد نتائج</p>
            <p className="text-sm text-gray-400">جرب تغيير معايير البحث</p>
          </div>
        ) : (
          <AnimatePresence>
            <div className="space-y-3">
              {providers.map((provider, index) => (
                <motion.div
                  key={provider.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: index * 0.04 }}
                >
                  <ProviderCard
                    provider={provider}
                    index={index}
                    onNavigate={() => navigate(`/provider-public/${provider.id}`)}
                  />
                </motion.div>
              ))}
            </div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

/* ── Provider Card ── */
function ProviderCard({ provider, index, onNavigate }) {
  const isTop = index === 0;

  return (
    <div
      className={`bg-white rounded-2xl border transition-all duration-200 hover:shadow-md hover:border-gray-300 overflow-hidden ${
        isTop ? 'border-amber-200' : 'border-gray-200'
      }`}
    >
      {/* Top badge */}
      {isTop && (
        <div className="flex items-center gap-1.5 px-4 py-2 bg-amber-50 border-b border-amber-100">
          <FiAward size={13} className="text-amber-500" />
          <span className="text-xs font-bold text-amber-700">الأعلى تقييماً</span>
        </div>
      )}

      <div className="p-4 flex items-center gap-4">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <img
            src={provider.avatarUrl || 'https://via.placeholder.com/150'}
            alt={provider.fullName}
            className="w-14 h-14 rounded-xl object-cover border border-gray-200"
          />
          {provider.available && (
            <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white" />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-sm font-bold text-gray-900 truncate">{provider.fullName}</span>
            {provider.isVerified && (
              <span className="flex-shrink-0 w-4 h-4 bg-blue-100 rounded-full flex items-center justify-center">
                <FiCheck size={10} className="text-blue-600" />
              </span>
            )}
          </div>

          <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
            <FiBriefcase size={11} />
            <span className="truncate">{provider.professionName}</span>
            <span className="text-gray-300">·</span>
            <FiMapPin size={11} />
            <span className="truncate">{provider.stateName}</span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Rating */}
            <div className="flex items-center gap-1 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-md">
              <FiStar size={11} className="text-amber-500 fill-current" />
              <span className="text-xs font-bold text-amber-700">
                {provider.averageRating?.toFixed(1) || '0.0'}
              </span>
            </div>

            {/* Availability */}
            <span
              className={`text-xs font-semibold px-2 py-0.5 rounded-md border ${
                provider.available
                  ? 'bg-green-50 border-green-100 text-green-700'
                  : 'bg-gray-50 border-gray-200 text-gray-500'
              }`}
            >
              {provider.available ? 'متاح' : 'غير متاح'}
            </span>
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={onNavigate}
          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-gray-900 hover:bg-gray-800 text-white text-xs font-bold rounded-xl transition-all active:scale-95"
        >
          <span>الملف</span>
          <FiArrowLeft size={13} className="rotate-180" />
        </button>
      </div>
    </div>
  );
}

export default ProvidersListScreen;